import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import apiClient from '../api/apiClient';
import { extractErrorMessage } from '../api/extractErrorMessage';

/**
 * @typedef {'hline' | 'vline' | 'crossline' | 'text' | 'trendline' | 'ray' | 'rectangle' | 'emoji' | 'timerange' | 'arrow' | 'channel' | 'fibRetracement' | 'measure' | 'alert_line' | 'brush' | 'pitchfork'} AnnotationType
 * @typedef {'solid' | 'dashed' | 'dotted'} LineStyle
 * 
 * @typedef {Object} AnnotationBase
 * @property {string} id
 * @property {AnnotationType} type
 * @property {string} symbol
 * @property {string} timeframe
 * @property {number} createdAt
 * @property {number} updatedAt
 * 
 * @typedef {AnnotationBase & {
 *   type: 'hline',
 *   price: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   showPrice: boolean
 * }} HLineAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'vline',
 *   time: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   showTime: boolean
 * }} VLineAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'text',
 *   time: number,
 *   price: number,
 *   text: string,
 *   color: string,
 *   backgroundColor?: string,
 *   fontSize: number
 * }} TextAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'crossline',
 *   time: number,
 *   price: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   showPrice: boolean,
 *   showTime: boolean
 * }} CrossLineAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'trendline',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   extendLeft: boolean,
 *   extendRight: boolean
 * }} TrendLineAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'ray',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string
 * }} RayAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'rectangle',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   color: string,
 *   fillOpacity: number,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string
 * }} RectangleAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'emoji',
 *   time: number,
 *   price: number,
 *   emoji: string,
 *   size: number
 * }} EmojiAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'timerange',
 *   time1: number,
 *   time2: number,
 *   color: string,
 *   fillOpacity: number,
 *   label?: string
 * }} TimeRangeAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'arrow',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   text: string,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   fontSize: number
 * }} ArrowAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'channel',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   priceOffset: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   fillOpacity: number,
 *   extendLeft: boolean,
 *   extendRight: boolean
 * }} ChannelAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'fibRetracement',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   fillOpacity: number,
 *   levels: number[],
 *   showExtensions: boolean,
 *   extendRight: boolean
 * }} FibRetracementAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'measure',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   showPriceDelta: boolean,
 *   showTimeDelta: boolean,
 *   showPercentage: boolean,
 *   showDistance: boolean
 * }} MeasureAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'alert_line',
 *   price: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   showPrice: boolean,
 *   triggered: boolean,
 *   direction: 'above' | 'below',
 *   alertId?: string
 * }} AlertLineAnnotation
 * 
 * @typedef {{ t: number, v: number }} BrushPoint
 * 
 * @typedef {AnnotationBase & {
 *   type: 'brush',
 *   points: BrushPoint[],
 *   color: string,
 *   lineWidth: number
 * }} BrushAnnotation
 * 
 * @typedef {AnnotationBase & {
 *   type: 'pitchfork',
 *   time1: number,
 *   price1: number,
 *   time2: number,
 *   price2: number,
 *   time3: number,
 *   price3: number,
 *   color: string,
 *   lineStyle: LineStyle,
 *   lineWidth: number,
 *   label?: string,
 *   extendRight: boolean
 * }} PitchforkAnnotation
 * 
 * @typedef {HLineAnnotation | VLineAnnotation | CrossLineAnnotation | TextAnnotation | TrendLineAnnotation | RayAnnotation | RectangleAnnotation | EmojiAnnotation | TimeRangeAnnotation | ArrowAnnotation | ChannelAnnotation | FibRetracementAnnotation | MeasureAnnotation | AlertLineAnnotation | BrushAnnotation | PitchforkAnnotation} Annotation
 */

// ─── Shape transforms ───────────────────────────────────────────────

/** Fields that live at the top level (not inside `data`) on the API side */
const TOP_LEVEL_KEYS = new Set([
  'annotationId', 'id', 'symbol', 'timeframe', 'type',
  'visible', 'locked', 'origin', 'createdAt', 'updatedAt',
]);

/**
 * Convert an API annotation into the flat shape the frontend expects.
 *   - Spreads `data` fields to the top level
 *   - Renames `annotationId` → `id`
 */
const apiToFrontend = (apiAnnotation) => {
  const { annotationId, data, ...rest } = apiAnnotation;
  return {
    ...rest,
    ...(data || {}),
    id: annotationId ?? rest.id,
  };
};

/**
 * Convert a flat frontend annotation into the API shape.
 *   - Extracts type-specific fields into `data`
 *   - Renames `id` → `annotationId`
 */
const frontendToApi = (annotation) => {
  const { id, ...rest } = annotation;
  const top = {};
  const data = {};
  for (const [key, value] of Object.entries(rest)) {
    if (TOP_LEVEL_KEYS.has(key)) {
      top[key] = value;
    } else {
      data[key] = value;
    }
  }
  return { annotationId: id, ...top, data };
};

/**
 * Extract only the `data`-level fields from a partial update object.
 * Used when PATCHing — the API expects updates inside `data`.
 */
const extractDataFields = (updates) => {
  const data = {};
  for (const [key, value] of Object.entries(updates)) {
    if (!TOP_LEVEL_KEYS.has(key)) {
      data[key] = value;
    }
  }
  return data;
};

// ─── API helpers ─────────────────────────────────────────────────────

const fetchAnnotations = async (symbol, timeframe) => {
  const res = await apiClient.get('/annotations', { params: { symbol, timeframe } });
  const list = res.data?.annotations ?? [];
  return list.map(apiToFrontend);
};

const createAnnotationAPI = async (symbol, timeframe, annotation) => {
  const { data } = frontendToApi(annotation);
  const res = await apiClient.post('/annotations', {
    symbol,
    timeframe,
    type: annotation.type,
    data,
  });
  return apiToFrontend(res.data);
};

const updateAnnotationAPI = async (annotationId, symbol, timeframe, patch) => {
  const data = extractDataFields(patch);
  const body = { symbol, timeframe };
  if (Object.keys(data).length > 0) body.data = data;
  // Include any top-level fields that were updated (visible, locked, etc.)
  for (const [key, value] of Object.entries(patch)) {
    if (TOP_LEVEL_KEYS.has(key) && key !== 'id' && key !== 'annotationId') {
      body[key] = value;
    }
  }
  await apiClient.put(`/annotations/${annotationId}`, body);
};

const deleteAnnotationAPI = async (annotationId, symbol, timeframe) => {
  await apiClient.delete(`/annotations/${annotationId}`, {
    params: { symbol, timeframe },
  });
};

// ─── Alert API helpers ──────────────────────────────────────────────

// POST /v2/alerts arms a price alert; a failure throws, so the line can show it is not armed. No label:
// a line's label can change without re-arming, so the alert would carry a stale one.
const createAlertAPI = async (symbol, price, direction) => {
  const res = await apiClient.post('/alerts', {
    symbol,
    condition: direction === 'above' ? 'price_above' : 'price_below',
    value: price,
  });
  const alertId = res.data?.alertId;
  if (!alertId) throw new Error('No alertId in the response');
  return alertId;
};

// A 404 means the alert is already gone (fired and cleared, or removed elsewhere): nothing to remove.
const deleteAlertAPI = async (alertId) => {
  try {
    await apiClient.delete(`/alerts/${alertId}`);
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
  }
};

// ─── Utilities ───────────────────────────────────────────────────────

/**
 * Generate a temporary client-side ID for optimistic inserts.
 * Will be replaced by the server-generated annotationId.
 */
const generateTempId = (prefix) => {
  const now = Date.now();
  const random = Math.random().toString(36).slice(2, 9);
  return `_tmp_${prefix}-${now}-${random}`;
};

/**
 * Default color palette for annotations
 */
export const ANNOTATION_COLORS = [
  '#ef4444', // red
  '#f97316', // orange  
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#64748b', // slate
  '#ffffff', // white
];

// ─── Debounce helper ─────────────────────────────────────────────────

const DEBOUNCE_MS = 300;

// ─── Path simplification (Ramer-Douglas-Peucker) ─────────────────────

/**
 * Perpendicular distance from point P to line segment AB.
 * Points are {t, v} in chart coordinate space.
 */
/**
 * Perpendicular distance using normalized coordinates.
 * Time and price have wildly different scales (ms timestamps vs price values),
 * so we normalize both to [0,1] range before computing distance.
 */
const perpendicularDistance = (p, a, b, normT, normV) => {
  const dx = (b.t - a.t) * normT;
  const dy = (b.v - a.v) * normV;
  const px = (p.t - a.t) * normT;
  const py = (p.v - a.v) * normV;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.sqrt(px * px + py * py);
  }
  const t = Math.max(0, Math.min(1, (px * dx + py * dy) / lenSq));
  const projX = t * dx;
  const projY = t * dy;
  const ddx = px - projX;
  const ddy = py - projY;
  return Math.sqrt(ddx * ddx + ddy * ddy);
};

/**
 * Ramer-Douglas-Peucker path simplification.
 * Reduces the number of points while preserving the shape.
 * @param {BrushPoint[]} points - Ordered array of {t, v} points
 * @param {number} epsilon - Tolerance (in chart coordinate units)
 * @returns {BrushPoint[]} Simplified points
 */
const simplifyPath = (points, epsilon, normT, normV) => {
  if (points.length <= 2) return points;

  // Find the point with the maximum distance
  let maxDist = 0;
  let maxIndex = 0;
  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last, normT, normV);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }

  // If max distance exceeds epsilon, recursively simplify
  if (maxDist > epsilon) {
    const left = simplifyPath(points.slice(0, maxIndex + 1), epsilon, normT, normV);
    const right = simplifyPath(points.slice(maxIndex), epsilon, normT, normV);
    return [...left.slice(0, -1), ...right];
  }

  // Otherwise, just keep the endpoints
  return [first, last];
};

/**
 * Compute a reasonable epsilon for path simplification based on
 * the price range of the points. Aims to keep the path recognizable
 * while removing noise.
 */
/**
 * Compute normalization factors and epsilon for path simplification.
 * Normalizes time and price to [0,1] range so they contribute equally.
 * Returns { epsilon, normT, normV }.
 */
const computeSimplificationParams = (points) => {
  if (points.length < 2) return { epsilon: 0, normT: 1, normV: 1 };
  let minV = Infinity, maxV = -Infinity;
  let minT = Infinity, maxT = -Infinity;
  for (const p of points) {
    if (p.v < minV) minV = p.v;
    if (p.v > maxV) maxV = p.v;
    if (p.t < minT) minT = p.t;
    if (p.t > maxT) maxT = p.t;
  }
  const rangeV = maxV - minV || 1;
  const rangeT = maxT - minT || 1;
  const normT = 1 / rangeT;
  const normV = 1 / rangeV;
  // Epsilon in normalized space — 0.5% of the unit diagonal
  const epsilon = Math.SQRT2 * 0.005;
  return { epsilon, normT, normV };
};

// ─── Hook ────────────────────────────────────────────────────────────

/**
 * Hook for managing chart annotations with API persistence
 * 
 * @param {string} symbol - Current symbol (e.g., 'BTCUSD')
 * @param {string} timeframe - Current timeframe (e.g., 'daily', 'hourly')
 * @param {Object} [options] - Additional options
 * @param {string[]} [options.availableTimeframes] - All timeframes for this symbol (for cross-timeframe visibility)
 * @returns {Object} Annotation state and handlers
 */
const EMPTY_TIMEFRAMES = [];

export default function useChartAnnotations(symbol, timeframe, { availableTimeframes } = {}) {
  // Stabilize the availableTimeframes reference to avoid infinite re-render loops
  const tfKey = availableTimeframes?.join(',') || '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTimeframes = useMemo(() => availableTimeframes || EMPTY_TIMEFRAMES, [tfKey]);

  // Annotations for the current symbol+timeframe (plus cross-timeframe visible ones)
  const [annotations, setAnnotations] = useState([]);
  // Ref to latest annotations for use in async callbacks (avoids stale closures)
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  // Loading state for initial fetch
  const [loading, setLoading] = useState(false);

  // Active tool for drawing new annotations
  const [activeTool, setActiveTool] = useState(null); // 'hline' | 'vline' | 'crossline' | 'text' | 'trendline' | 'ray' | 'rectangle' | 'emoji' | 'timerange' | 'arrow' | 'channel' | 'fibRetracement' | 'measure' | 'alert_line' | 'brush' | 'pitchfork' | null
  
  // Currently selected annotation for editing
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  
  // Pending text annotation (waiting for text input)
  const [pendingTextAnnotation, setPendingTextAnnotation] = useState(null);
  
  // Two-point placement state (trend line, ray, etc.)
  // Stores the first anchor point while waiting for the second click.
  const [pendingTwoPoint, setPendingTwoPoint] = useState(null);
  const pendingTwoPointRef = useRef(null);
  pendingTwoPointRef.current = pendingTwoPoint;


  // Preview endpoint for two-point placement (updated on mouse move)
  const [twoPointPreview, setTwoPointPreview] = useState(null);
  // Brush drawing state: accumulates points during a freehand stroke
  // { points: BrushPoint[], isDrawing: boolean }
  const [brushStroke, setBrushStroke] = useState(null);
  const brushStrokeRef = useRef(null); // mutable ref for mousemove perf
  
  // Edit popover state
  const [editPopoverAnnotation, setEditPopoverAnnotation] = useState(null);

  // Two-point tool state: first click anchor for rectangle, trend line, arrow, etc.
  const [pendingFirstPoint, setPendingFirstPoint] = useState(null); // { time, price }
  const pendingFirstPointRef = useRef(null);
  pendingFirstPointRef.current = pendingFirstPoint;

  // Preview annotation for two-point tools (updated on mouse hover)
  const [previewAnnotation, setPreviewAnnotation] = useState(null);

  // Debounce timers for update calls (keyed by annotation id)
  const debounceTimers = useRef({});

  // Track current symbol/timeframe to avoid stale fetches
  const currentKey = useRef(null);

  /**
   * Fetch annotations for all available timeframes and merge them.
   * An annotation is included if its visibleTimeframes is empty/undefined
   * (meaning "All") or explicitly includes the current timeframe.
   * This applies equally to annotations from the current and other timeframes,
   * so users can hide an annotation from its source timeframe if desired.
   */
  const fetchAndMerge = useCallback(async (sym, currentTf, allTimeframes) => {
    const timeframesToFetch = [currentTf, ...allTimeframes.filter(tf => tf !== currentTf)];
    const results = await Promise.allSettled(
      timeframesToFetch.map(tf => fetchAnnotations(sym, tf))
    );
    const merged = [];
    const seenIds = new Set();
    for (let i = 0; i < results.length; i++) {
      if (results[i].status !== 'fulfilled') continue;
      const sourceTf = timeframesToFetch[i];
      const isCurrentTf = sourceTf === currentTf;
      for (const ann of results[i].value) {
        if (seenIds.has(ann.id)) continue;
        // Check visibility: no visibleTimeframes (or empty) = visible on all
        const vt = ann.visibleTimeframes;
        const visibleOnCurrent = !vt || vt.length === 0 || vt.includes(currentTf);
        if (visibleOnCurrent) {
          merged.push({ ...ann, _sourceTimeframe: sourceTf });
          seenIds.add(ann.id);
        }
      }
    }
    return merged;
  }, []);

  // ── Fetch on mount / symbol+timeframe change ──────────────────────

  useEffect(() => {
    if (!symbol || !timeframe) return;

    const key = `${symbol}:${timeframe}`;
    currentKey.current = key;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const merged = await fetchAndMerge(symbol, timeframe, stableTimeframes);
        if (!cancelled && currentKey.current === key) {
          setAnnotations(merged);
        }
      } catch (err) {
        console.warn('Failed to fetch annotations:', err);
        if (!cancelled && currentKey.current === key) {
          setAnnotations([]);
        }
      } finally {
        if (!cancelled && currentKey.current === key) {
          setLoading(false);
        }
      }
    };

    load();

    return () => { cancelled = true; };
  }, [symbol, timeframe, stableTimeframes, fetchAndMerge]);

  // Clear selection when symbol/timeframe changes
  useEffect(() => {
    setSelectedAnnotationId(null);
    setActiveTool(null);
    setPendingTextAnnotation(null);
    setPendingTwoPoint(null);
    setTwoPointPreview(null);
    setBrushStroke(null);
    brushStrokeRef.current = null;
    setEditPopoverAnnotation(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);
  }, [symbol, timeframe]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = debounceTimers;
    return () => {
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  // ── Debounced API update ──────────────────────────────────────────

  const setActiveToolSafe = useCallback((tool) => {
    if (tool !== activeTool) {
      setPendingTwoPoint(null);
      setTwoPointPreview(null);
      setPendingTextAnnotation(null);
      setPendingFirstPoint(null);
      setPreviewAnnotation(null);
    }
    setActiveTool(tool);
  }, [activeTool]);

  // Alerts this session replaced or cancelled. The chart's alert list is read once per symbol, so these
  // are hidden from its alert guides rather than drawn as if they were still armed.
  const [retiredAlertIds, setRetiredAlertIds] = useState(() => new Set());
  const retireAlert = useCallback((alertId) => {
    if (alertId) setRetiredAlertIds((prev) => new Set(prev).add(alertId));
  }, []);

  // An alert line is armed when the alert it owns exists (its alertId). Arming replaces that alert, for a
  // new line, a moved one, a changed direction or a retry. Arms for one line run one at a time: a request
  // made while one is running is queued, and the queued arm reads the line as it is by then, with the
  // alert the previous arm left it owning (state may not have re-rendered yet).
  // _alertError and _alertArming are local only, never persisted.
  const armQueue = useRef({});
  const armAlertLineOnce = useCallback(async (ann) => {
    const { id } = ann;
    const annTimeframe = ann._sourceTimeframe || timeframe;
    const apply = (patch) => {
      setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
      setEditPopoverAnnotation((prev) => (prev?.id === id ? { ...prev, ...patch } : prev));
    };
    if (ann.alertId) {
      try {
        await deleteAlertAPI(ann.alertId);
        retireAlert(ann.alertId);
      } catch (err) {
        apply({ _alertError: `Previous alert not removed, so it is still set at the old level: ${extractErrorMessage(err)}` });
        return ann.alertId;
      }
    }
    let alertId = null;
    let alertError = null;
    try {
      alertId = await createAlertAPI(symbol, ann.price, ann.direction);
    } catch (err) {
      alertError = extractErrorMessage(err);
    }
    try {
      await updateAnnotationAPI(id, symbol, annTimeframe, { alertId, triggered: false });
    } catch (err) {
      // The line does not own the new alert, so the server could never cancel it: remove it now. If that
      // fails too, the line keeps the alert locally, so Retry replaces it rather than adding a second.
      let owned = null;
      if (alertId) {
        try {
          await deleteAlertAPI(alertId);
          retireAlert(alertId);
        } catch {
          owned = alertId;
        }
      }
      apply({ alertId: owned, triggered: false, _alertError: `Alert not saved on the line: ${extractErrorMessage(err)}` });
      return owned;
    }
    apply({ alertId, triggered: false, _alertError: alertError });
    return alertId;
  }, [symbol, timeframe, retireAlert]);

  const armAlertLine = useCallback(async (ann) => {
    const queue = armQueue.current;
    if (queue[ann.id]) {
      queue[ann.id].again = true;
      return;
    }
    queue[ann.id] = { again: false };
    const setArming = (arming) => {
      setAnnotations((prev) => prev.map((a) => (a.id === ann.id ? { ...a, _alertArming: arming } : a)));
      setEditPopoverAnnotation((prev) => (prev?.id === ann.id ? { ...prev, _alertArming: arming } : prev));
    };
    setArming(true);
    try {
      let current = ann;
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const ownedAlertId = await armAlertLineOnce(current);
        if (!queue[ann.id].again) break;
        queue[ann.id].again = false;
        const latest = annotationsRef.current.find((a) => a.id === ann.id);
        if (!latest) break;
        current = { ...latest, alertId: ownedAlertId };
      }
    } finally {
      delete queue[ann.id];
      setArming(false);
    }
  }, [armAlertLineOnce]);

  const rearmAlertLine = useCallback((id) => {
    const ann = annotationsRef.current.find((a) => a.id === id);
    if (!ann || ann.type !== 'alert_line' || id.startsWith('_tmp_')) return;
    armAlertLine(ann);
  }, [armAlertLine]);

  const debouncedUpdate = useCallback((id, patch) => {
    // Clear any existing timer for this annotation
    if (debounceTimers.current[id]) {
      clearTimeout(debounceTimers.current[id]);
    }
    debounceTimers.current[id] = setTimeout(async () => {
      delete debounceTimers.current[id];
      // Skip API calls for temp/preview IDs (not yet created on server)
      if (id.startsWith('_tmp_') || id.startsWith('_preview_')) return;
      try {
        // Use the annotation's source timeframe for the API call (may differ from current view)
        const ann = annotationsRef.current.find(a => a.id === id);
        const annTimeframe = ann?._sourceTimeframe || timeframe;
        await updateAnnotationAPI(id, symbol, annTimeframe, patch);
        
        if (ann?.type === 'alert_line' && (patch.price !== undefined || patch.direction !== undefined)) {
          await armAlertLine({ ...ann, ...patch });
        }
      } catch (err) {
        console.warn('Failed to update annotation:', err);
        // Refetch all timeframes to reconcile
        try {
          const merged = await fetchAndMerge(symbol, timeframe, stableTimeframes);
          setAnnotations(merged);
        } catch (_) { /* swallow */ }
      }
    }, DEBOUNCE_MS);
  }, [symbol, timeframe, stableTimeframes, fetchAndMerge, armAlertLine]);

  // ── CRUD operations ───────────────────────────────────────────────

  /**
   * Shared helper: optimistic insert + API persist + rollback on failure.
   * Builds the annotation from the provided properties, assigns a temp ID,
   * inserts it optimistically, then replaces the temp ID on success or
   * rolls back on failure.
   */
  const createAnnotationOptimistic = useCallback((type, props) => {
    const now = Date.now();
    const tempId = generateTempId(type);
    const annotation = {
      id: tempId,
      type,
      symbol,
      timeframe,
      ...props,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn(`Failed to create ${type} annotation:`, err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new horizontal line annotation
   */
  const createHLineAnnotation = useCallback((price, options = {}) => {
    return createAnnotationOptimistic('hline', {
      price,
      color: options.color ?? '#ef4444',
      lineStyle: options.lineStyle ?? 'dashed',
      lineWidth: options.lineWidth ?? 1.5,
      label: options.label ?? '',
      showPrice: options.showPrice ?? true,
    });
  }, [createAnnotationOptimistic]);

  /**
   * Create a new vertical line annotation
   */
  const createVLineAnnotation = useCallback((time, options = {}) => {
    return createAnnotationOptimistic('vline', {
      time,
      color: options.color ?? '#3b82f6',
      lineStyle: options.lineStyle ?? 'dashed',
      lineWidth: options.lineWidth ?? 1.5,
      label: options.label ?? '',
      showTime: options.showTime ?? true,
    });
  }, [createAnnotationOptimistic]);

  /**
   * Create a discretionary anchored-VWAP annotation (Mode B). The user clicks
   * one candle; `anchorTime` is that candle's timestamp and the VWAP line +
   * σ-bands are computed client-side from the anchor forward. Chart-only — it
   * persists like any annotation but generates no systematic events.
   */
  const createAnchoredVwapAnnotation = useCallback((anchorTime, options = {}) => {
    return createAnnotationOptimistic('anchored_vwap', {
      anchorTime,
      resolution: options.resolution ?? 'both', // '1' | '2' | 'both' bands
      color: options.color ?? '#B388FF',
      bandColor: options.bandColor ?? '#9575CD',
      lineWidth: options.lineWidth ?? 1.75,
    });
  }, [createAnnotationOptimistic]);

  /**
   * Create a new text annotation
   */
  const createTextAnnotation = useCallback((time, price, text, options = {}) => {
    const { screenX, screenY, ...annotationOptions } = options;
    const annotation = createAnnotationOptimistic('text', {
      time,
      price,
      text: text || 'Note',
      color: annotationOptions.color ?? '#f5f5f5',
      backgroundColor: annotationOptions.backgroundColor,
      fontSize: annotationOptions.fontSize ?? 12,
    });
    setPendingTextAnnotation(null);
    
    const position = (screenX !== undefined && screenY !== undefined) 
      ? { x: screenX, y: screenY } 
      : undefined;
    
    return { annotation, position };
  }, [createAnnotationOptimistic]);

  const createTwoPointAnnotation = useCallback((type, time1, price1, time2, price2, options = {}) => {
    const { screenX, screenY, ...annotationOptions } = options;
    const annotation = createAnnotationOptimistic(type, {
      time1,
      price1,
      time2,
      price2,
      color: annotationOptions.color,
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      label: annotationOptions.label ?? '',
      ...(annotationOptions.extendLeft !== undefined ? { extendLeft: annotationOptions.extendLeft } : {}),
      ...(annotationOptions.extendRight !== undefined ? { extendRight: annotationOptions.extendRight } : {}),
    });

    // Clear two-point placement state
    setPendingTwoPoint(null);
    setTwoPointPreview(null);

    return annotation;
  }, [createAnnotationOptimistic]);

  /**
   * Create a new trend line annotation (two-point)
   */
  const createTrendLineAnnotation = useCallback((time1, price1, time2, price2, options = {}) => (
    createTwoPointAnnotation('trendline', time1, price1, time2, price2, {
      color: '#3b82f6',
      extendLeft: false,
      extendRight: false,
      ...options,
    })
  ), [createTwoPointAnnotation]);

  /**
   * Create a new ray annotation (two-point, extends from point 2 onwards)
   */
  const createRayAnnotation = useCallback((time1, price1, time2, price2, options = {}) => (
    createTwoPointAnnotation('ray', time1, price1, time2, price2, {
      color: '#f97316',
      ...options,
    })
  ), [createTwoPointAnnotation]);

  /**
   * Create a new cross line annotation (horizontal + vertical intersection)
   */
  const createCrossLineAnnotation = useCallback((time, price, options = {}) => {
    const { screenX, screenY, ...annotationOptions } = options;
    return createAnnotationOptimistic('crossline', {
      time,
      price,
      color: annotationOptions.color ?? '#06b6d4',
      lineStyle: annotationOptions.lineStyle ?? 'dashed',
      lineWidth: annotationOptions.lineWidth ?? 1,
      label: annotationOptions.label ?? '',
      showPrice: annotationOptions.showPrice ?? true,
      showTime: annotationOptions.showTime ?? true,
    });
  }, [createAnnotationOptimistic]);

  /**
   * Create a new rectangle (price range) annotation
   */
  const createRectangleAnnotation = useCallback((time1, price1, time2, price2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('rect');
    const annotation = {
      id: tempId,
      type: 'rectangle',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      color: annotationOptions.color ?? '#3b82f6',
      fillOpacity: annotationOptions.fillOpacity ?? 0.15,
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      label: annotationOptions.label ?? '',
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create rectangle annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new emoji annotation
   */
  const createEmojiAnnotation = useCallback((time, price, emoji, options = {}) => {
    const { screenX, screenY, ...annotationOptions } = options;
    return createAnnotationOptimistic('emoji', {
      time,
      price,
      emoji: emoji || '🔥',
      size: annotationOptions.size ?? 24,
    });
  }, [createAnnotationOptimistic]);

  /**
   * Create a new time range (date/time highlight) annotation
   */
  const createTimeRangeAnnotation = useCallback((time1, time2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('timerange');
    const annotation = {
      id: tempId,
      type: 'timerange',
      symbol,
      timeframe,
      time1,
      time2,
      color: annotationOptions.color ?? '#8b5cf6',
      fillOpacity: annotationOptions.fillOpacity ?? 0.10,
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1,
      label: annotationOptions.label ?? '',
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create time range annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new arrow / callout annotation (two-point: text box → arrow target)
   */
  const createArrowAnnotation = useCallback((time1, price1, time2, price2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('arrow');
    const annotation = {
      id: tempId,
      type: 'arrow',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      text: annotationOptions.text ?? 'Note',
      color: annotationOptions.color ?? '#f97316',
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      fontSize: annotationOptions.fontSize ?? 12,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create arrow annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new channel annotation (two-point + offset)
   */
  const createChannelAnnotation = useCallback((time1, price1, time2, price2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('channel');
    // Default offset: 5% of price range between the two points, or a small fraction of price1
    const defaultOffset = Math.abs(price2 - price1) * 0.5 || price1 * 0.02;
    const annotation = {
      id: tempId,
      type: 'channel',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      priceOffset: annotationOptions.priceOffset ?? defaultOffset,
      color: annotationOptions.color ?? '#8b5cf6',
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      label: annotationOptions.label ?? '',
      fillOpacity: annotationOptions.fillOpacity ?? 0.08,
      extendLeft: annotationOptions.extendLeft ?? false,
      extendRight: annotationOptions.extendRight ?? false,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create channel annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new fibonacci retracement annotation
   */
  const createFibRetracementAnnotation = useCallback((time1, price1, time2, price2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('fibRetracement');
    const annotation = {
      id: tempId,
      type: 'fibRetracement',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      color: annotationOptions.color ?? '#f59e0b',
      lineStyle: annotationOptions.lineStyle ?? 'solid',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      label: annotationOptions.label ?? '',
      fillOpacity: annotationOptions.fillOpacity ?? 0.06,
      levels: annotationOptions.levels ?? [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1],
      showExtensions: annotationOptions.showExtensions ?? false,
      extendRight: annotationOptions.extendRight ?? true,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create fibonacci retracement annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new measure annotation (two-point distance measuring tool)
   */
  const createMeasureAnnotation = useCallback((time1, price1, time2, price2, options = {}) => {
    const now = Date.now();
    const { screenX, screenY, ...annotationOptions } = options;
    const tempId = generateTempId('measure');
    const annotation = {
      id: tempId,
      type: 'measure',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      color: annotationOptions.color ?? '#64748b',
      lineStyle: annotationOptions.lineStyle ?? 'dashed',
      lineWidth: annotationOptions.lineWidth ?? 1.5,
      showPriceDelta: annotationOptions.showPriceDelta ?? true,
      showTimeDelta: annotationOptions.showTimeDelta ?? true,
      showPercentage: annotationOptions.showPercentage ?? true,
      showDistance: annotationOptions.showDistance ?? true,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Note: Measure annotations are local-only, not persisted to API

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Compute target/stop from the two-click placement based on which side of
   * entry the user's second click lands on. Returns the same default 2:1 R:R
   * regardless of click direction:
   *   long, second click above entry → target = secondPrice, stop = half-distance below.
   *   long, second click below entry → stop = secondPrice, target = 2×-distance above.
   *   short mirrors this.
   * The user can drag any leg afterward to refine.
   */
  const derivePositionLegs = (direction, entryPrice, secondPrice, overrides = {}) => {
    const wantsClickAsTarget = direction === 'long'
      ? secondPrice >= entryPrice
      : secondPrice <= entryPrice;
    if (wantsClickAsTarget) {
      const targetPrice = secondPrice;
      const halfReward = Math.abs(secondPrice - entryPrice) / 2;
      const stopPrice = overrides.stopPrice
        ?? (direction === 'long' ? entryPrice - halfReward : entryPrice + halfReward);
      return { targetPrice, stopPrice };
    }
    const stopPrice = secondPrice;
    const doubleRisk = Math.abs(entryPrice - secondPrice) * 2;
    const targetPrice = overrides.targetPrice
      ?? (direction === 'long' ? entryPrice + doubleRisk : entryPrice - doubleRisk);
    return { targetPrice, stopPrice };
  };

  /**
   * Create a long-position risk/reward annotation. Optimistic insert + API
   * persist, so the position survives page reloads.
   */
  const createLongPositionAnnotation = useCallback((time1, entryPrice, time2, secondPrice, options = {}) => {
    const { screenX: _sx, screenY: _sy, ...rest } = options;
    const { targetPrice, stopPrice } = derivePositionLegs('long', entryPrice, secondPrice, rest);
    const annotation = createAnnotationOptimistic('long_position', {
      time1,
      time2,
      entryPrice,
      stopPrice,
      targetPrice,
      label: rest.label ?? '',
    });
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);
    return annotation;
  }, [createAnnotationOptimistic]);

  /**
   * Create a short-position risk/reward annotation (mirror of the long
   * variant). Optimistic insert + API persist.
   */
  const createShortPositionAnnotation = useCallback((time1, entryPrice, time2, secondPrice, options = {}) => {
    const { screenX: _sx, screenY: _sy, ...rest } = options;
    const { targetPrice, stopPrice } = derivePositionLegs('short', entryPrice, secondPrice, rest);
    const annotation = createAnnotationOptimistic('short_position', {
      time1,
      time2,
      entryPrice,
      stopPrice,
      targetPrice,
      label: rest.label ?? '',
    });
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);
    return annotation;
  }, [createAnnotationOptimistic]);

  /**
   * Create a new alert line annotation (horizontal line wired to DWLF alert system)
   */
  const createAlertLineAnnotation = useCallback((price, options = {}) => {
    const now = Date.now();
    const tempId = generateTempId('alert_line');
    const direction = options.direction ?? 'above';
    const annotation = {
      id: tempId,
      type: 'alert_line',
      symbol,
      timeframe,
      price,
      color: options.color ?? '#f59e0b',
      lineStyle: options.lineStyle ?? 'dashed',
      lineWidth: options.lineWidth ?? 1.5,
      label: options.label ?? '',
      showPrice: options.showPrice ?? true,
      triggered: false,
      direction,
      alertId: null,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);

    // Persist annotation to API + create alert
    createAnnotationAPI(symbol, timeframe, annotation)
      .then(async (saved) => {
        // Replace temp id with server-generated id
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );

        await armAlertLine({ ...annotation, ...saved, id: saved.id });
      })
      .catch((err) => {
        console.warn('Failed to create alert line annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe, armAlertLine]);

  /**
   * Create a new brush (freehand drawing) annotation from accumulated points.
   * Applies RDP simplification to reduce point count.
   */
  const createBrushAnnotation = useCallback((rawPoints, options = {}) => {
    if (!rawPoints || rawPoints.length < 2) return null;

    // Simplify the path
    const { epsilon, normT, normV } = computeSimplificationParams(rawPoints);
    const simplified = simplifyPath(rawPoints, epsilon, normT, normV);
    // Ensure minimum 2 points after simplification
    if (simplified.length < 2) return null;

    const now = Date.now();
    const tempId = generateTempId('brush');
    const annotation = {
      id: tempId,
      type: 'brush',
      symbol,
      timeframe,
      points: simplified,
      color: options.color ?? '#3b82f6',
      lineWidth: options.lineWidth ?? 2,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create brush annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Create a new pitchfork annotation (three-point: base line + handle point)
   */
  const createPitchforkAnnotation = useCallback((time1, price1, time2, price2, time3, price3, options = {}) => {
    const now = Date.now();
    const tempId = generateTempId('pitchfork');
    const annotation = {
      id: tempId,
      type: 'pitchfork',
      symbol,
      timeframe,
      time1,
      price1,
      time2,
      price2,
      time3,
      price3,
      color: options.color ?? '#ec4899',
      lineStyle: options.lineStyle ?? 'solid',
      lineWidth: options.lineWidth ?? 1.5,
      label: options.label ?? '',
      extendRight: options.extendRight ?? true,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic insert
    setAnnotations((prev) => [...prev, annotation]);
    setSelectedAnnotationId(annotation.id);
    setEditPopoverAnnotation(annotation);
    setActiveTool(null);
    setPreviewAnnotation(null);
    setPendingTwoPoint(null);
    setTwoPointPreview(null);

    // Persist to API
    createAnnotationAPI(symbol, timeframe, annotation)
      .then((saved) => {
        setAnnotations((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, ...saved, id: saved.id } : a))
        );
        setSelectedAnnotationId((prev) => (prev === tempId ? saved.id : prev));
        setEditPopoverAnnotation((prev) =>
          prev?.id === tempId ? { ...prev, ...saved, id: saved.id } : prev
        );
      })
      .catch((err) => {
        console.warn('Failed to create pitchfork annotation:', err);
        setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
        setSelectedAnnotationId((prev) => (prev === tempId ? null : prev));
        setEditPopoverAnnotation((prev) => (prev?.id === tempId ? null : prev));
      });

    return annotation;
  }, [symbol, timeframe]);

  /**
   * Update an existing annotation
   */
  const updateAnnotation = useCallback((id, updates) => {
    const updatedAt = Date.now();
    
    // Optimistic local update
    setAnnotations((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, ...updates, updatedAt } : a
      )
    );
    
    // Refresh editPopoverAnnotation if it's the one being updated
    if (editPopoverAnnotation?.id === id) {
      setEditPopoverAnnotation((prev) => 
        prev ? { ...prev, ...updates, updatedAt } : null
      );
    }

    // Debounced API update (handles drag operations)
    debouncedUpdate(id, updates);
  }, [editPopoverAnnotation, debouncedUpdate]);

  /**
   * Delete an annotation
   */
  const deleteAnnotation = useCallback((id) => {
    // Capture for potential rollback
    const deleted = annotations.find((a) => a.id === id);

    // Optimistic removal
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedAnnotationId === id) {
      setSelectedAnnotationId(null);
    }
    if (editPopoverAnnotation?.id === id) {
      setEditPopoverAnnotation(null);
    }

    // Cancel any pending debounced update for this annotation
    if (debounceTimers.current[id]) {
      clearTimeout(debounceTimers.current[id]);
      delete debounceTimers.current[id];
    }

    // Only call API if it's a persisted annotation (not a temp id)
    if (deleted && !id.startsWith('_tmp_')) {
      const annTimeframe = deleted._sourceTimeframe || timeframe;
      deleteAnnotationAPI(id, symbol, annTimeframe).catch((err) => {
        console.warn('Failed to delete annotation:', err);
        // Rollback — add it back
        if (deleted) {
          setAnnotations((prev) => [...prev, deleted]);
        }
      });
      // The server cancels an alert line's alert when the annotation is deleted (DWLF-580).
      if (deleted.type === 'alert_line') retireAlert(deleted.alertId);
    }
  }, [annotations, selectedAnnotationId, editPopoverAnnotation, symbol, timeframe, retireAlert]);

  // ── Interaction handlers ──────────────────────────────────────────

  /**
   * Handle chart click when placing annotations.
   * For brush: click (mousedown) starts the stroke.
   */
  const handleChartClick = useCallback((clickInfo) => {
    if (!activeTool) return false;
    
    const { time, price, screenX, screenY } = clickInfo;

    if (activeTool === 'vline') {
      if (!Number.isFinite(time)) return false;
      createVLineAnnotation(time);
      return { type: 'vline' };
    }

    // Anchored VWAP — one-point, time-anchored (no price needed): the clicked
    // candle is the anchor; the line + bands are computed from there forward.
    if (activeTool === 'anchored_vwap') {
      if (!Number.isFinite(time)) return false;
      createAnchoredVwapAnnotation(time);
      return { type: 'anchored_vwap' };
    }

    // Price is required for hline and text annotations
    if (!Number.isFinite(price)) return false;

    // Use ref to get latest pendingTwoPoint and avoid stale closure on rapid clicks
    let pending = pendingTwoPointRef.current;

    if (pending && pending.type !== activeTool) {
      setPendingTwoPoint(null);
      setTwoPointPreview(null);
      pending = null;
    }

    if (activeTool === 'hline') {
      createHLineAnnotation(price);
      return { type: 'hline' };
    }

    if (activeTool === 'crossline') {
      if (!Number.isFinite(time)) return false;
      createCrossLineAnnotation(time, price, { screenX, screenY });
      return { type: 'crossline' };
    }

    if (activeTool === 'text') {
      if (!Number.isFinite(time)) return false;
      setPendingTextAnnotation({ time, price, screenX, screenY });
      return { type: 'text' };
    }

    if (activeTool === 'emoji') {
      if (!Number.isFinite(time)) return false;
      createEmojiAnnotation(time, price, '🔥', { screenX, screenY });
      return { type: 'emoji' };
    }

    // Two-point tools (trend line, ray, etc.)
    if (activeTool === 'trendline' || activeTool === 'ray') {
      if (!Number.isFinite(time)) return false;
      if (pending) {
        // Second click — complete the annotation
        if (pending.type === 'ray') {
          createRayAnnotation(
            pending.time1,
            pending.price1,
            time,
            price,
            { screenX, screenY },
          );
          return { type: 'ray' };
        }
        createTrendLineAnnotation(
          pending.time1,
          pending.price1,
          time,
          price,
          { screenX, screenY },
        );
        return { type: 'trendline' };
      }
      // First click — store anchor
      setPendingTwoPoint({ type: activeTool, time1: time, price1: price });
      setTwoPointPreview({ time: time, price: price });
      return { type: activeTool, pending: true };
    }

    // Two-point tool: rectangle
    if (activeTool === 'rectangle') {
      if (!Number.isFinite(time)) return false;
      // Use ref to get latest pendingFirstPoint and avoid stale closure on rapid clicks
      const currentPending = pendingFirstPointRef.current;
      if (!currentPending) {
        // First click: store anchor point
        setPendingFirstPoint({ time, price });
        return { type: 'rectangle', step: 'first' };
      }
      // Second click: create the rectangle
      createRectangleAnnotation(
        currentPending.time,
        currentPending.price,
        time,
        price,
        { screenX, screenY },
      );
      return { type: 'rectangle', step: 'complete' };
    }

    // Two-point tool: time range highlight
    if (activeTool === 'timerange') {
      if (!Number.isFinite(time)) return false;
      if (!pendingFirstPoint) {
        // First click: store anchor time
        setPendingFirstPoint({ time, price });
        return { type: 'timerange', step: 'first' };
      }
      // Second click: create the time range
      createTimeRangeAnnotation(
        pendingFirstPoint.time,
        time,
        { screenX, screenY },
      );
      return { type: 'timerange', step: 'complete' };
    }

    // Two-point tool: arrow / callout
    if (activeTool === 'arrow') {
      if (!Number.isFinite(time)) return false;
      // Use ref pattern to avoid race conditions on rapid clicks
      const currentPending = pendingFirstPointRef.current;
      if (!currentPending) {
        // First click: store text box anchor
        setPendingFirstPoint({ time, price });
        return { type: 'arrow', step: 'first' };
      }
      // Second click: create the arrow (first point = text box, second = arrow target)
      createArrowAnnotation(
        currentPending.time,
        currentPending.price,
        time,
        price,
        { screenX, screenY },
      );
      return { type: 'arrow', step: 'complete' };
    }

    // Two-point tool: channel
    if (activeTool === 'channel') {
      if (!Number.isFinite(time)) return false;
      if (pendingFirstPoint) {
        // Second click — complete the annotation
        createChannelAnnotation(
          pendingFirstPoint.time,
          pendingFirstPoint.price,
          time,
          price,
          { screenX, screenY },
        );
        return { type: 'channel' };
      }
      // First click — store anchor
      setPendingFirstPoint({ time, price });
      return { type: 'channel', pending: true };
    }

    // ── Fibonacci Retracement (two-point) ──
    if (activeTool === 'fibRetracement') {
      if (!Number.isFinite(time)) return false;
      const currentPending = pendingFirstPointRef.current; // Avoid stale closure on rapid clicks
      if (currentPending) {
        // Second click — complete the annotation
        createFibRetracementAnnotation(
          currentPending.time,
          currentPending.price,
          time,
          price,
          { screenX, screenY }
        );
        return { type: 'fibRetracement' };
      }
      // First click — store anchor
      setPendingFirstPoint({ time, price });
      return { type: 'fibRetracement', pending: true };
    }

    // ── Measure Tool (two-point) ──
    if (activeTool === 'measure') {
      if (!Number.isFinite(time)) return false;
      const currentPending = pendingFirstPointRef.current; // Avoid stale closure on rapid clicks
      if (currentPending) {
        // Second click — complete the annotation
        createMeasureAnnotation(
          currentPending.time,
          currentPending.price,
          time,
          price,
          { screenX, screenY }
        );
        return { type: 'measure' };
      }
      // First click — store anchor
      setPendingFirstPoint({ time, price });
      return { type: 'measure', pending: true };
    }

    // ── Long/Short Position Tools (two-point: entry → target) ──
    if (activeTool === 'long_position' || activeTool === 'short_position') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) return false;
      const currentPending = pendingFirstPointRef.current;
      if (currentPending) {
        if (activeTool === 'long_position') {
          createLongPositionAnnotation(
            currentPending.time, currentPending.price,
            time, price,
            { screenX, screenY },
          );
          return { type: 'long_position' };
        }
        createShortPositionAnnotation(
          currentPending.time, currentPending.price,
          time, price,
          { screenX, screenY },
        );
        return { type: 'short_position' };
      }
      setPendingFirstPoint({ time, price });
      return { type: activeTool, pending: true };
    }

    if (activeTool === 'alert_line') {
      createAlertLineAnnotation(price);
      return { type: 'alert_line' };
    }

    if (activeTool === 'brush') {
      // Start a new brush stroke
      const initial = [{ t: time, v: price }];
      brushStrokeRef.current = initial;
      setBrushStroke({ points: initial, isDrawing: true });
      return { type: 'brush', pending: true };
    }

    // Three-point tool: pitchfork
    if (activeTool === 'pitchfork') {
      if (!Number.isFinite(time)) return false;
      const currentPending = pendingTwoPointRef.current;
      
      if (!currentPending) {
        // First click: start base line
        setPendingTwoPoint({ type: 'pitchfork', time1: time, price1: price });
        return { type: 'pitchfork', step: 'first' };
      } else if (!currentPending.time2) {
        // Second click: complete base line, wait for handle
        setPendingTwoPoint({ 
          type: 'pitchfork', 
          time1: currentPending.time1, 
          price1: currentPending.price1,
          time2: time,
          price2: price
        });
        return { type: 'pitchfork', step: 'second' };
      } else {
        // Third click: complete pitchfork
        createPitchforkAnnotation(
          currentPending.time1,
          currentPending.price1,
          currentPending.time2,
          currentPending.price2,
          time,
          price,
          { screenX, screenY }
        );
        return { type: 'pitchfork', step: 'complete' };
      }
    }

    return false;
  }, [activeTool, createHLineAnnotation, createVLineAnnotation, createCrossLineAnnotation, createTrendLineAnnotation, createRayAnnotation, createRectangleAnnotation, createEmojiAnnotation, createTimeRangeAnnotation, createArrowAnnotation, createChannelAnnotation, createFibRetracementAnnotation, createMeasureAnnotation, createLongPositionAnnotation, createShortPositionAnnotation, createAlertLineAnnotation, createPitchforkAnnotation, createAnchoredVwapAnnotation, pendingFirstPoint]);

  /**
   * Handle chart hover for live preview of two-point annotations.
   * Called from DWLFChart's onChartCanvasHover.
   */
  const handleChartHover = useCallback((hoverInfo) => {
    // Without an anchor point we have nothing to preview, even if a tool is
    // active (user has selected the tool but not yet placed the first point).
    // The activeTool branches below all read pendingFirstPoint.time, which
    // would NPE without this guard.
    if (!pendingFirstPoint && !pendingTwoPointRef.current) {
      if (previewAnnotation) setPreviewAnnotation(null);
      return;
    }

    if (!hoverInfo) {
      setPreviewAnnotation(null);
      return;
    }

    const { time, price } = hoverInfo;
    
    if (activeTool === 'rectangle') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      setPreviewAnnotation({
        id: '_preview_',
        type: 'rectangle',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        price1: pendingFirstPoint.price,
        time2: time,
        price2: price,
        color: '#3b82f6',
        fillOpacity: 0.1,
        lineStyle: 'dashed',
        lineWidth: 1,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'timerange') {
      if (!Number.isFinite(time)) {
        setPreviewAnnotation(null);
        return;
      }
      setPreviewAnnotation({
        id: '_preview_',
        type: 'timerange',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        time2: time,
        color: '#8b5cf6',
        fillOpacity: 0.08,
        lineStyle: 'dashed',
        lineWidth: 1,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'arrow') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      setPreviewAnnotation({
        id: '_preview_',
        type: 'arrow',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        price1: pendingFirstPoint.price,
        time2: time,
        price2: price,
        text: '',
        color: '#f97316',
        lineStyle: 'dashed',
        lineWidth: 1,
        fontSize: 12,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'channel') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      const defaultOffset = Math.abs(price - pendingFirstPoint.price) * 0.5 || pendingFirstPoint.price * 0.02;
      setPreviewAnnotation({
        id: '_preview_',
        type: 'channel',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        price1: pendingFirstPoint.price,
        time2: time,
        price2: price,
        priceOffset: defaultOffset,
        color: '#8b5cf6',
        fillOpacity: 0.06,
        lineStyle: 'dashed',
        lineWidth: 1.5,
        label: '',
        extendLeft: false,
        extendRight: false,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'fibRetracement') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      setPreviewAnnotation({
        id: '_preview_fib',
        type: 'fibRetracement',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        price1: pendingFirstPoint.price,
        time2: time,
        price2: price,
        color: '#f59e0b',
        lineStyle: 'dashed',
        lineWidth: 1,
        label: '',
        fillOpacity: 0.04,
        levels: [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1],
        showExtensions: false,
        extendRight: true,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'measure') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      setPreviewAnnotation({
        id: '_preview_measure',
        type: 'measure',
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        price1: pendingFirstPoint.price,
        time2: time,
        price2: price,
        color: '#64748b',
        lineStyle: 'dashed',
        lineWidth: 1.5,
        showPriceDelta: true,
        showTimeDelta: true,
        showPercentage: true,
        showDistance: true,
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'long_position' || activeTool === 'short_position') {
      if (!Number.isFinite(time) || !Number.isFinite(price)) {
        setPreviewAnnotation(null);
        return;
      }
      const direction = activeTool === 'long_position' ? 'long' : 'short';
      const { targetPrice, stopPrice } = derivePositionLegs(direction, pendingFirstPoint.price, price);
      setPreviewAnnotation({
        id: activeTool === 'long_position' ? '_preview_long_position' : '_preview_short_position',
        type: activeTool,
        symbol,
        timeframe,
        time1: pendingFirstPoint.time,
        time2: time,
        entryPrice: pendingFirstPoint.price,
        stopPrice,
        targetPrice,
        label: '',
        createdAt: 0,
        updatedAt: 0,
      });
    } else if (activeTool === 'pitchfork') {
      // For pitchfork, use pendingTwoPoint for multi-stage placement
      const pitchforkPending = pendingTwoPointRef.current;
      if (pitchforkPending && pitchforkPending.type === 'pitchfork') {
        if (!Number.isFinite(time) || !Number.isFinite(price)) {
          setPreviewAnnotation(null);
          return;
        }
        
        if (!pitchforkPending.time2) {
          // First stage: preview base line
          setPreviewAnnotation({
            id: '_preview_pitchfork_base',
            type: 'trendline', // Use trendline for base line preview
            symbol,
            timeframe,
            time1: pitchforkPending.time1,
            price1: pitchforkPending.price1,
            time2: time,
            price2: price,
            color: '#ec4899',
            lineStyle: 'dashed',
            lineWidth: 1,
            extendLeft: false,
            extendRight: false,
            createdAt: 0,
            updatedAt: 0,
          });
        } else {
          // Second stage: preview full pitchfork
          setPreviewAnnotation({
            id: '_preview_pitchfork',
            type: 'pitchfork',
            symbol,
            timeframe,
            time1: pitchforkPending.time1,
            price1: pitchforkPending.price1,
            time2: pitchforkPending.time2,
            price2: pitchforkPending.price2,
            time3: time,
            price3: price,
            color: '#ec4899',
            lineStyle: 'dashed',
            lineWidth: 1.5,
            label: '',
            extendRight: true,
            createdAt: 0,
            updatedAt: 0,
          });
        }
      }
    }
  }, [pendingFirstPoint, activeTool, previewAnnotation, symbol, timeframe]);

  /**
   * Complete pending text annotation with actual text
   */
  const completePendingTextAnnotation = useCallback((text) => {
    if (!pendingTextAnnotation) return undefined;
    const { time, price, screenX, screenY } = pendingTextAnnotation;
    return createTextAnnotation(time, price, text, { screenX, screenY });
  }, [pendingTextAnnotation, createTextAnnotation]);

  /**
   * Cancel pending text annotation
   */
  const cancelPendingTextAnnotation = useCallback(() => {
    setPendingTextAnnotation(null);
    setActiveTool(null);
  }, []);

  /**
   * Update preview endpoint during two-point placement (called on mouse move)
   */
  const handleAnnotationMouseMove = useCallback((moveInfo) => {
    if (!pendingTwoPoint) return;
    const { time, price } = moveInfo;
    if (Number.isFinite(time) && Number.isFinite(price)) {
      setTwoPointPreview({ time, price });
    }
  }, [pendingTwoPoint]);

  /**
   * Compute preview annotation for two-point placement.
   * This gets merged into the annotations array for rendering.
   */
  const twoPointPreviewAnnotation = (() => {
    if (!pendingTwoPoint || !twoPointPreview) return null;
    const now = Date.now();
    const previewBase = {
      symbol,
      timeframe,
      time1: pendingTwoPoint.time1,
      price1: pendingTwoPoint.price1,
      time2: twoPointPreview.time,
      price2: twoPointPreview.price,
      lineStyle: 'dashed',
      lineWidth: 1.5,
      label: '',
      createdAt: now,
      updatedAt: now,
    };
    if (pendingTwoPoint.type === 'trendline') {
      return {
        ...previewBase,
        id: '_preview_trendline',
        type: 'trendline',
        color: '#3b82f6',
        extendLeft: false,
        extendRight: false,
      };
    }
    if (pendingTwoPoint.type === 'ray') {
      return {
        ...previewBase,
        id: '_preview_ray',
        type: 'ray',
        color: '#f97316',
      };
    }
    if (pendingTwoPoint.type === 'pitchfork') {
      // During pitchfork placement, show appropriate preview based on stage
      if (!pendingTwoPoint.time2) {
        // Stage 1: show base line preview
        return {
          ...previewBase,
          id: '_preview_pitchfork_stage1',
          type: 'trendline',
          color: '#ec4899',
          extendLeft: false,
          extendRight: false,
        };
      } else if (pendingTwoPoint.time3 === undefined) {
        // Stage 2: show partial pitchfork (need third point for completion)
        return {
          id: '_preview_pitchfork_stage2',
          type: 'pitchfork',
          symbol,
          timeframe,
          time1: pendingTwoPoint.time1,
          price1: pendingTwoPoint.price1,
          time2: pendingTwoPoint.time2,
          price2: pendingTwoPoint.price2,
          time3: twoPointPreview.time,
          price3: twoPointPreview.price,
          color: '#ec4899',
          lineStyle: 'dashed',
          lineWidth: 1.5,
          label: '',
          extendRight: true,
          createdAt: now,
          updatedAt: now,
        };
      }
    }
    return null;
  })();

  /**
   * Compute a preview brush annotation for rendering during drawing.
   */
  const brushPreviewAnnotation = (() => {
    if (!brushStroke || !brushStroke.isDrawing || !brushStroke.points || brushStroke.points.length < 2) {
      return null;
    }
    return {
      id: '_preview_brush',
      type: 'brush',
      points: brushStroke.points,
      lineWidth: 2,
      color: '#ffffff',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  })();

  /**
   * Toggle the hidden state of an annotation
   * Uses functional update pattern to avoid stale closure issues
   */
  const toggleAnnotationVisibility = useCallback((id) => {
    setAnnotations((prev) => {
      const annotation = prev.find((a) => a.id === id);
      if (!annotation) return prev;
      
      const newHidden = annotation.hidden !== true;
      
      // Persist to API (use current value from prev, not stale closure)
      debouncedUpdate(id, { hidden: newHidden });
      
      return prev.map((a) =>
        a.id === id ? { ...a, hidden: newHidden, updatedAt: Date.now() } : a
      );
    });
  }, [debouncedUpdate]);

  /**
   * Visible annotations: filter out hidden ones for chart rendering
   */
  const visibleAnnotations = useMemo(() => 
    annotations.filter((a) => a.hidden !== true),
    [annotations]
  );

  /**
   * Combined annotations: visible annotations + optional preview (two-point or brush)
   */
  const activePreview = previewAnnotation || twoPointPreviewAnnotation || brushPreviewAnnotation;
  const annotationsWithPreview = activePreview
    ? [...visibleAnnotations, activePreview]
    : visibleAnnotations;

  /**
   * Cancel two-point placement without deselecting tool (consistent with trendline/ray ESC behavior)
   */
  const cancelTwoPointPlacement = useCallback(() => {
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);
    setPendingTwoPoint(null);
    setTwoPointPreview(null);
  }, []);

    /**
   * Handle mouse move during brush drawing.
   * Accumulates points into the stroke. Uses ref for performance.
   * @param {{ time: number, price: number }} moveInfo
   */
  const handleBrushMouseMove = useCallback((moveInfo) => {
    if (!brushStrokeRef.current) return;
    const { time, price } = moveInfo;
    if (!Number.isFinite(time) || !Number.isFinite(price)) return;
    brushStrokeRef.current = [...brushStrokeRef.current, { t: time, v: price }];
    // Throttle React state updates — update every 3rd point for smooth rendering
    if (brushStrokeRef.current.length % 3 === 0) {
      setBrushStroke({ points: [...brushStrokeRef.current], isDrawing: true });
    }
  }, []);

  /**
   * Handle mouse up to finish brush drawing.
   * Simplifies the path and creates the annotation.
   */
  const handleBrushMouseUp = useCallback(() => {
    const points = brushStrokeRef.current;
    brushStrokeRef.current = null;
    setBrushStroke(null);
    if (points && points.length >= 2) {
      createBrushAnnotation(points);
    }
  }, [createBrushAnnotation]);

  /**
   * Select an annotation (highlight only, no popover)
   */
  const selectAnnotation = useCallback((id) => {
    setSelectedAnnotationId(id);
    setEditPopoverAnnotation(null);
  }, []);

  /**
   * Open annotation popover (select + show popover)
   */
  const openAnnotationPopover = useCallback((id) => {
    setSelectedAnnotationId(id);
    if (id) {
      const annotation = annotations.find((a) => a.id === id);
      if (annotation) {
        setEditPopoverAnnotation(annotation);
      } else {
        setEditPopoverAnnotation((prev) => (prev?.id === id ? prev : null));
      }
    } else {
      setEditPopoverAnnotation(null);
    }
  }, [annotations]);

  /**
   * Deselect current annotation
   */
  const deselectAnnotation = useCallback(() => {
    setSelectedAnnotationId(null);
    setEditPopoverAnnotation(null);
  }, []);

  /**
   * Clear all annotations for current symbol/timeframe
   */
  const clearAnnotations = useCallback(() => {
    const toDelete = annotations.filter((a) => !a.id.startsWith('_tmp_'));
    
    // Optimistic clear
    setAnnotations([]);
    setSelectedAnnotationId(null);
    setEditPopoverAnnotation(null);
    setPendingTwoPoint(null);
    setTwoPointPreview(null);
    setPendingFirstPoint(null);
    setPreviewAnnotation(null);

    // Cancel all pending debounced updates
    Object.keys(debounceTimers.current).forEach((key) => {
      clearTimeout(debounceTimers.current[key]);
      delete debounceTimers.current[key];
    });

    // Delete each from API using its source timeframe; the server cancels each alert line's alert.
    toDelete.forEach((a) => {
      const annTimeframe = a._sourceTimeframe || timeframe;
      if (a.type === 'alert_line') retireAlert(a.alertId);
      deleteAnnotationAPI(a.id, symbol, annTimeframe).catch((err) => {
        console.warn('Failed to delete annotation during clear:', err);
      });
    });
  }, [annotations, symbol, timeframe, retireAlert]);

  // ── Keyboard shortcuts ────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (pendingTwoPoint) {
          // Cancel two-point placement (go back to tool selected but no anchor)
          setPendingTwoPoint(null);
          setTwoPointPreview(null);
        } else if (pendingFirstPoint && activeTool === 'rectangle') {
          // Cancel rectangle placement (go back to tool selected but no anchor)
          cancelTwoPointPlacement();
        } else if (pendingFirstPoint) {
          // Cancel two-point placement (clear first anchor)
          cancelTwoPointPlacement();
        } else if (brushStroke?.isDrawing) {
          // Cancel active brush stroke
          brushStrokeRef.current = null;
          setBrushStroke(null);
        } else if (pendingTextAnnotation) {
          cancelPendingTextAnnotation();
        } else if (activeTool) {
          setActiveTool(null);
        } else if (selectedAnnotationId) {
          deselectAnnotation();
        }
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedAnnotationId) {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        deleteAnnotation(selectedAnnotationId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    activeTool,
    selectedAnnotationId,
    pendingTextAnnotation,
    pendingTwoPoint,
    pendingFirstPoint,
    brushStroke,
    cancelPendingTextAnnotation,
    cancelTwoPointPlacement,
    deselectAnnotation,
    deleteAnnotation,
  ]);

  return {
    // State
    annotations: annotationsWithPreview,
    allAnnotations: annotations, // includes hidden annotations (for sidebar panel)
    activeTool,
    selectedAnnotationId,
    pendingTextAnnotation,
    pendingTwoPoint,
    pendingFirstPoint,
    brushStroke,
    editPopoverAnnotation,
    previewAnnotation,
    loading,
    
    // Derived state
    isPlacing: activeTool !== null,
    isPlacingTwoPoint: pendingTwoPoint !== null || pendingFirstPoint !== null,
    
    // Tool selection
    setActiveTool: setActiveToolSafe,
    
    // CRUD operations
    updateAnnotation,
    deleteAnnotation,
    rearmAlertLine,
    retiredAlertIds,
    clearAnnotations,
    toggleAnnotationVisibility,
    
    // Interaction handlers
    handleChartClick,
    handleAnnotationMouseMove,
    handleChartHover,
    selectAnnotation,
    openAnnotationPopover,
    deselectAnnotation,
    
    // Text annotation helpers
    completePendingTextAnnotation,
    cancelPendingTextAnnotation,
    
    // Two-point placement helpers
    cancelTwoPointPlacement,
    // Brush drawing handlers
    handleBrushMouseMove,
    handleBrushMouseUp,
    // Brush state
    isBrushDrawing: brushStroke?.isDrawing ?? false,
  };
}